// RLHoops CSV Parser Service
// Parses tournament results CSV files into structured data for the ranking system.

export interface ParsedEvent {
  eventName: string;
  eventDate: string;
  ageGroups: ParsedAgeGroup[];
}

export interface ParsedAgeGroup {
  ageGroup: string;
  category: 'high_school' | 'youth';
  pools: ParsedPool[];
  games: ParsedGame[];
}

export interface ParsedPool {
  letter: string;
  teams: { name: string; wins: number; losses: number }[];
}

export interface ParsedGame {
  gameNumber: number;
  dateTime: string;
  location: string;
  team1Name: string;
  team1Pool: string;
  team1Score: number;
  team2Name: string;
  team2Pool: string;
  team2Score: number;
  winnerName: string;
  loserName: string;
  pointDifferential: number;
  isBlowout: boolean;
}

export function extractPoolAndName(rawName: string): { name: string; pool: string } {
  const poolMatch = rawName.match(/\(([A-Z])\)\s*$/);
  if (poolMatch) {
    return {
      pool: poolMatch[1],
      name: rawName.replace(/\s*\([A-Z]\)\s*$/, '').trim()
    };
  }
  return { pool: '?', name: rawName.trim() };
}

function cleanLine(line: string): string {
  return line.replace(/,\s*$/, '').trim();
}

function isAgeGroupHeader(line: string): boolean {
  return /^(11|12|13|14|15|16|17)u$/i.test(line);
}

function parseAgeGroupCategory(ag: string): 'high_school' | 'youth' {
  const num = parseInt(ag.replace(/u/i, ''), 10);
  return num <= 14 ? 'youth' : 'high_school';
}

function isPoolLetter(line: string): boolean {
  return /^[A-Z]$/.test(line);
}

function isGameHeaderLine(line: string): boolean {
  return /^GameNumber/i.test(line);
}

function parseGameLine(line: string): ParsedGame | null {
  // Split by comma, but respect that team names won't have commas
  const parts = line.split(',').map(p => p.trim());
  // Expect at least 7 fields: GameNumber, DateTime, Location, Team1, Score1, Team2, Score2
  if (parts.length < 7) return null;

  const gameNumber = parseInt(parts[0], 10);
  if (isNaN(gameNumber)) return null;

  const dateTime = parts[1];
  const location = parts[2];
  const rawTeam1 = parts[3];
  const score1 = parseInt(parts[4], 10);
  const rawTeam2 = parts[5];
  const score2 = parseInt(parts[6], 10);

  if (isNaN(score1) || isNaN(score2)) return null;

  const t1 = extractPoolAndName(rawTeam1);
  const t2 = extractPoolAndName(rawTeam2);

  const diff = Math.abs(score1 - score2);
  const team1Wins = score1 > score2;

  return {
    gameNumber,
    dateTime,
    location,
    team1Name: t1.name,
    team1Pool: t1.pool,
    team1Score: score1,
    team2Name: t2.name,
    team2Pool: t2.pool,
    team2Score: score2,
    winnerName: team1Wins ? t1.name : t2.name,
    loserName: team1Wins ? t2.name : t1.name,
    pointDifferential: diff,
    isBlowout: diff >= 20,
  };
}

export function parseRLHoopsCsv(csvContent: string): ParsedEvent {
  const rawLines = csvContent.split(/\r?\n/);
  const lines = rawLines.map(cleanLine);

  // Line 1: event name, Line 2: date
  const eventName = lines[0] || '';
  const eventDate = lines[1] || '';

  const ageGroups: ParsedAgeGroup[] = [];

  // Find age group section boundaries
  const sectionStarts: { index: number; ageGroup: string }[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (isAgeGroupHeader(lines[i])) {
      sectionStarts.push({ index: i, ageGroup: lines[i].toLowerCase() });
    }
  }

  for (let s = 0; s < sectionStarts.length; s++) {
    const start = sectionStarts[s].index + 1;
    const end = s + 1 < sectionStarts.length ? sectionStarts[s + 1].index : lines.length;
    const ag = sectionStarts[s].ageGroup;

    const pools: ParsedPool[] = [];
    const games: ParsedGame[] = [];
    let currentPool: ParsedPool | null = null;
    let inGames = false;

    for (let i = start; i < end; i++) {
      const line = lines[i];
      if (!line) continue;

      // Check for game results section
      if (isGameHeaderLine(line)) {
        inGames = true;
        // Flush any pending pool
        if (currentPool) { pools.push(currentPool); currentPool = null; }
        continue;
      }

      if (inGames) {
        const game = parseGameLine(line);
        if (game) games.push(game);
        continue;
      }

      // Pool standings parsing
      if (isPoolLetter(line)) {
        if (currentPool) pools.push(currentPool);
        currentPool = { letter: line, teams: [] };
        continue;
      }

      // Skip "Team,W,L" header
      if (/^Team\s*,\s*W\s*,\s*L/i.test(line)) continue;

      // Team standing line: "Name,W,L"
      if (currentPool) {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length >= 3) {
          const teamName = parts[0];
          const wins = parseInt(parts[1], 10);
          const losses = parseInt(parts[2], 10);
          if (teamName && !isNaN(wins) && !isNaN(losses)) {
            currentPool.teams.push({ name: teamName, wins, losses });
          }
        }
      }
    }

    // Flush last pool
    if (currentPool) pools.push(currentPool);

    ageGroups.push({
      ageGroup: ag,
      category: parseAgeGroupCategory(ag),
      pools,
      games,
    });
  }

  return { eventName, eventDate, ageGroups };
}
